/**
 * Bordered response shell matching the Thinking panel: header (agent name) +
 * body (draggable-resizable scrollable content).
 *
 * Per-turn ``usage`` chip in the header mirrors the BuilderStreamTerminal's
 * bottom StatusBar: model · ↓in ↑out · $cost. Worker emits it with the
 * AGENT_FINAL_RESPONSE event so the chip renders the moment the response
 * lands — no separate fetch.
 */
import React, { useState, useCallback } from 'react';
import { Copy, GripHorizontal } from 'lucide-react';
import PromptCaptureLink from './PromptCaptureLink.jsx';
import { formatAgentMessageForCopy } from '@/utils/agentMessageFormatting';
import { useResizable } from '@stepflow/ui-shared/hooks/useResizable';
import { humanizeAgentName } from '@stepflow/ui-shared/utils/agentDisplayName.js';
import { MODEL_ID_TO_FAMILY, MODEL_ROWS } from '@stepflow/ui-shared/services/generatedPricing';
import AgentMessageContent from './AgentMessageContent.jsx';
import ThinkingTerminal from './ThinkingTerminal.jsx';

/** Format a token count compactly (1234 → "1.2k", 1_500_000 → "1.5M"). */
function _fmtTokens(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
}

/** Friendly model label matching BuilderStreamTerminal's StatusBar:
 *  ``global.anthropic.claude-haiku-4-5-20251001-v1:0`` → ``"claude haiku 4 5"``.
 *  Falls back to the family label ("Reason"/"Think"/"Fast") when the model
 *  id isn't in ``MODEL_ID_TO_FAMILY`` so legacy events still render. */
function _modelLabel(modelId) {
  if (!modelId) return '';
  const short = String(modelId)
    .replace(/^global\.anthropic\./, '')
    .replace(/^anthropic\./, '')
    .replace(/^us\.anthropic\./, '')
    .replace(/^eu\.anthropic\./, '')
    .replace(/-\d{8}-v\d.*$/, '')
    .replace(/-/g, ' ');
  if (short) return short;
  const fam = MODEL_ID_TO_FAMILY[modelId];
  return fam ? (MODEL_ROWS[fam]?.label ?? fam) : modelId;
}

/** What the request would have cost without prompt caching: every cache_read
 *  and cache_creation token billed at the full input rate. The delta vs.
 *  the actual charged cost is the cache savings. Computed locally because
 *  the AGENT_FINAL_RESPONSE event only carries the *charged* cost. */
function _estimateUncachedCost(modelId, inT, outT, cacheRead, cacheCreation) {
  const fam = MODEL_ID_TO_FAMILY[modelId];
  const row = fam ? MODEL_ROWS[fam] : null;
  if (!row) return null;
  return (
    ((inT + cacheRead + cacheCreation) / 1_000_000) * row.usd_input
    + (outT / 1_000_000) * row.usd_output
  );
}

/** Bottom status bar — matches BuilderStreamTerminal's StatusBar pattern.
 *  Surfaces the full picture: total tokens (uncached + cache_read +
 *  cache_creation), charged cost, and — when the request hit a cached
 *  prefix — the dollar amount saved by prompt caching. Without surfacing
 *  cache, users see a tiny ``inputTokens`` count and assume the model
 *  barely ran; in reality Sonnet routinely processes 30k–80k cached
 *  prefix tokens per turn. */
function UsageStatusBar({ usage }) {
  if (!usage || typeof usage !== 'object') return null;
  const inT = Number(usage.inputTokens) || 0;
  const outT = Number(usage.outputTokens) || 0;
  const cacheRead = Number(usage.cacheReadTokens) || 0;
  const cacheCreation = Number(usage.cacheCreationTokens) || 0;
  const totalIn = inT + cacheRead + cacheCreation;
  if (totalIn === 0 && outT === 0) return null;
  const cost = Number(usage.estimatedCostUsd) || 0;
  const label = _modelLabel(usage.modelId);

  let saved = 0;
  if (cacheRead > 0 || cacheCreation > 0) {
    const uncached = _estimateUncachedCost(usage.modelId, inT, outT, cacheRead, cacheCreation);
    if (uncached != null) saved = Math.max(0, uncached - cost);
  }

  // Compact breakdown — only the cached portion is interesting (it's where
  // 90%+ of tokens live on a hit). Cache_creation is implied by the saved $
  // chip; full breakdown stays in the tooltip.
  const breakdown = cacheRead > 0
    ? ` (${_fmtTokens(cacheRead)} cached)`
    : '';

  const parts = [];
  if (label) parts.push(label);
  parts.push(`$${cost.toFixed(4)}`);
  if (saved > 0.0001) parts.push(`saved $${saved.toFixed(4)} (cache)`);
  parts.push(`↓${_fmtTokens(totalIn)}${breakdown} ↑${_fmtTokens(outT)}`);
  if (usage.calls > 1) parts.push(`${usage.calls} calls`);
  // Points — the user-facing billing unit (1 pt = $0.001). Shown last so
  // the customer sees the unit they actually purchased, after the dollar
  // estimate. Stamped by the backend on every AGENT_FINAL_RESPONSE event.
  const points = Number(usage.points) || 0;
  if (points > 0) parts.push(`${points.toLocaleString()} pts`);

  return (
    <div className="border-t border-slate-200 px-3 py-1 text-[10px] tabular-nums text-slate-500 font-mono flex items-center gap-1 whitespace-nowrap">
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-slate-300">|</span>}
          <span className={p.startsWith('saved ') ? 'text-emerald-600' : ''}>{p}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Draggable resize hook ──────────────────────────────────────────────────

/**
 * @param {Object} props
 * @param {string} props.agentName
 * @param {string} props.text
 * @param {import('react').ReactNode} [props.headerRight]
 * @param {string} [props.contentKey]  when `text` is updated without reference change, pass a key
 * @param {import('@/utils/agentRoomReducer').AgentUsageSnapshot} [props.usage]
 *   Per-turn Bedrock usage; renders a chip in the header when present.
 */
export default function AgentResponsePanel({ agentName, text, headerRight, contentKey: _contentKey, thinking, turnStatus, startedAt, endedAt, activeAgentId, usage, networkId, poolId, taskId, unbounded = false, hideHeader = false, hideChrome = false, fill = false }) {
  const [copyHint, setCopyHint] = useState('');
  // ``unbounded`` panels (the final Summary) render at FULL height always — the deliverable
  // shouldn't be trapped in a small scroll window. Regular agent panels default to the compact
  // fixed-height window and expand on double-click.
  const [expanded, setExpanded] = useState(unbounded);
  const copyableText = formatAgentMessageForCopy(text);
  const canCopy = copyableText.length > 0;
  const { height, onMouseDown } = useResizable({ minHeight: 50, defaultHeight: 125, maxHeight: typeof window !== 'undefined' ? window.innerHeight * 0.6 : 400 });

  const handleHeaderDoubleClick = useCallback(() => {
    setExpanded((v) => !v);
  }, []);

  function copyMessage() {
    if (!canCopy || !navigator.clipboard) return;
    navigator.clipboard.writeText(copyableText).then(
      () => {
        setCopyHint('Copied');
        window.setTimeout(() => setCopyHint(''), 1500);
      },
      () => {
        setCopyHint('Copy failed');
        window.setTimeout(() => setCopyHint(''), 1500);
      },
    );
  }

  // ``hideChrome`` (used by the hierarchy panel): drop this component's OWN border/box + header so
  // it nests INSIDE the panel's single header/frame — no double header, no box-in-box. The panel
  // hosts the Context/Copy actions itself. ``hideHeader`` = keep the box but drop just the header.
  const actions = (
    <div className="ml-auto flex items-center gap-0.5">
      <PromptCaptureLink
        networkId={networkId}
        source="worker"
        agent={agentName}
        pool={poolId}
        task={taskId}
      />
      {canCopy ? (
        <button
          type="button"
          onClick={copyMessage}
          className={
            'inline-flex items-center justify-center rounded p-1 text-slate-500 transition-colors hover:bg-slate-100/80 hover:text-slate-800 ' +
            (copyHint === 'Copied'
              ? 'text-emerald-600'
              : copyHint === 'Copy failed'
                ? 'text-red-600'
                : '')
          }
          aria-label={copyHint || 'Copy message'}
          title={copyHint || 'Copy message'}
        >
          <Copy className="h-3 w-3 shrink-0" aria-hidden />
        </button>
      ) : null}
    </div>
  );

  return (
    // ONE FLAT WHITE SURFACE, NO OUTER FRAME — matches the run block. Was ``bg-slate-50/80
    // shadow-sm``, which only read as "a card" because the room behind it was a gray-blue
    // gradient; on the flat white room that tint + shadow made every message a floating gray tile.
    // The outer ``border`` went with them for the same reason the run block lost its own: a box
    // around each message is what makes the stream a stack of windows instead of one page. The
    // header/footer keep their ``border-b``/``border-t`` — those separate a message's own parts.
    <div className={fill
      // FILL (dock host): flex-fill the pane so the answer scrolls and the usage footer PINS to the
      // bottom (the dock owns the height — no own grip, no growing-past-the-footer).
      ? 'flex h-full min-h-0 min-w-0 flex-col overflow-hidden'
      : hideChrome
        ? 'min-w-0 overflow-hidden'
        : 'min-w-0 overflow-hidden rounded-lg bg-white'}>
      {(hideHeader || hideChrome) ? null : (
        <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-1.5 cursor-pointer select-none" onDoubleClick={handleHeaderDoubleClick}>
          <span className="text-[11px] font-semibold text-slate-800" title={agentName}>{humanizeAgentName(agentName)}</span>
          {headerRight ? (
            <span className="text-[10px] text-slate-500">{headerRight}</span>
          ) : null}
          {/* Right-aligned header actions: "Context" + Copy. */}
          {actions}
        </div>
      )}

      <div
        style={(fill || expanded || unbounded) ? {} : { height: `${height}px` }}
        className={`min-w-0 px-3 py-2 text-[12px] leading-relaxed text-slate-800 overflow-x-hidden [scrollbar-gutter:stable] ${
          fill ? 'min-h-0 flex-1 overflow-y-auto' : (expanded || unbounded) ? '' : 'overflow-y-auto'}`}
      >
        {thinking && thinking.length > 0 && (
          <div className="mb-2">
            <ThinkingTerminal turn={{ thinking, status: turnStatus || 'completed', activeAgentId, startedAt, endedAt }} />
          </div>
        )}
        <AgentMessageContent text={text} className="!mt-0" />
      </div>

      {/* Usage footer — pinned (shrink-0) in FILL mode so it stays at the pane's bottom. */}
      <div className={fill ? 'shrink-0' : ''}>
        <UsageStatusBar usage={usage} />
      </div>

      {/* Drag handle — hidden for unbounded (Summary) panels AND in FILL mode (the dock owns the
          height, so a second grip is redundant — matches the builder terminal). */}
      {(unbounded || fill) ? null : (
        <div
          onMouseDown={onMouseDown}
          className="flex items-center justify-center h-2 cursor-row-resize border-t border-slate-200/80 bg-slate-50/90 hover:bg-slate-100 transition-colors group"
        >
          <GripHorizontal className="h-2.5 w-3.5 text-slate-400 group-hover:text-slate-600" />
        </div>
      )}
    </div>
  );
}
